import { useState } from 'react';

import { CashFlowType, LifecycleStatus, ManagementType } from '@ndvr/portfolio-api';

import { useAssetClassExposureChart } from '@/cip/api/assetAllocation';
import { useHouseholdRoutePaths, useRequiredParams } from 'utils';
import { useAccountYtdTaxMetrics } from 'views/account/tax-metrics-queries';
import { useAllocation } from 'views/portfolio-engine/allocation-builder/allocations/allocation-queries';
import { useAccountGroupProfileWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { useListCashflowConfigs } from 'views/portfolio/account-group-queries';
import { useAccount, useListPortfolioEngineAccountsForHousehold } from 'views/portfolio/account-queries';
import { getNextWithdrawal } from 'views/portfolio/PortfolioPage/tabs/liquidity-planning/cash-flow-schedule';

import { getCurrentAllocation } from './current-allocation';
import { CurrentAllocationCard } from './CurrentAllocationCard';
import { NetValueCard } from './NetValueCard';
import { PortfolioBoostCard } from './PortfolioBoostCard';
import { TopPositionsCard } from './TopPositionsCard';
import { TradingOverviewCard } from './TradingOverviewCard';
import { WithdrawalPlanCard } from './WithdrawalPlanCard';

export const AccountOverviewTab = () => {
    const { globalAccountGUID, accountGroupGUID, accountGUID } = useRequiredParams([
        'globalAccountGUID',
        'accountGroupGUID',
        'accountGUID',
    ]);
    const { PortfolioPageRoutePath, AccountPageRoutePath, HouseholdIndividualPageRoutePath } = useHouseholdRoutePaths();
    const [dismissedBoostAccountGUID, setDismissedBoostAccountGUID] = useState<string>();
    const { data: account, isPending: isAccountPending } = useAccount(accountGUID);
    const { data: profile, isError: isProfileError } = useAccountGroupProfileWithAumIncluded(accountGroupGUID, {
        throwOnError: false,
    });
    const { data: engineAccounts = [] } = useListPortfolioEngineAccountsForHousehold(globalAccountGUID, undefined, {
        throwOnError: false,
    });
    const engineAccount = engineAccounts.find((item) => item.accountGUID === accountGUID);
    const managementType = engineAccount?.managementType ?? account?.managementType;
    const { data: modelAllocation, isError: isAllocationNameError } = useAllocation(
        managementType === ManagementType.AUTOMATIC
            ? profile?.accountGroup.allocationGUID || account?.allocationGUID
            : account?.allocationGUID,
        { throwOnError: false },
    );
    const showBoost =
        managementType !== undefined &&
        managementType !== ManagementType.AUTOMATIC &&
        dismissedBoostAccountGUID !== accountGUID;
    const enrollmentPath = `${PortfolioPageRoutePath(globalAccountGUID, accountGroupGUID)}/overview`;
    const {
        data: assetClassExposures,
        isLoading: isAllocationLoading,
        isError: isAllocationError,
    } = useAssetClassExposureChart(account?.brokerAccountId ? [account.brokerAccountId] : [], { throwOnError: false });
    const isAllocationUnavailable = !isAccountPending && !account?.brokerAccountId;
    const { data: taxMetrics, isLoading: isTaxMetricsLoading } = useAccountYtdTaxMetrics(
        account?.lifecycleStatus === LifecycleStatus.REAL ? accountGUID : undefined,
        { throwOnError: false },
    );
    const { data: cashFlowConfigs = [], isLoading: isWithdrawalLoading } = useListCashflowConfigs(
        accountGroupGUID,
        undefined,
        {
            throwOnError: false,
        },
    );
    const nextWithdrawal = getNextWithdrawal(
        cashFlowConfigs.filter(
            (config) => config.cashFlowType === CashFlowType.WITHDRAWAL && config.accountGUID === accountGUID,
        ),
    );

    return (
        <div className="grid items-stretch gap-6 pt-4 xl:grid-cols-[3fr_2fr]">
            <NetValueCard
                householdGUID={globalAccountGUID}
                accountGUID={accountGUID}
                performancePath={`${HouseholdIndividualPageRoutePath(globalAccountGUID, 'reporting')}?groupByGuids=${encodeURIComponent(accountGUID)}`}
            />
            {showBoost ? (
                <PortfolioBoostCard
                    enrollmentPath={enrollmentPath}
                    onDismiss={() => setDismissedBoostAccountGUID(accountGUID)}
                />
            ) : (
                <WithdrawalPlanCard
                    accountGUID={accountGUID}
                    accountGroupGUID={accountGroupGUID}
                    planningPath={`${PortfolioPageRoutePath(globalAccountGUID, accountGroupGUID)}/liquidity-planning`}
                />
            )}
            <CurrentAllocationCard
                allocation={getCurrentAllocation(assetClassExposures ?? [])}
                isLoading={isAccountPending || isAllocationLoading}
                isError={isAllocationError}
                isUnavailable={isAllocationUnavailable}
                allocationName={modelAllocation?.allocationName}
                isAllocationNameError={isAllocationNameError || isProfileError}
                allocationPath={`${PortfolioPageRoutePath(globalAccountGUID, accountGroupGUID)}/target-allocation`}
            />
            <TradingOverviewCard
                managementType={managementType}
                tradingStatus={engineAccount?.tradingStatus}
                cash={engineAccount?.cash}
                harvestableLoss={engineAccount?.hasTaxLossHarvesting ? engineAccount.harvestableLoss : undefined}
                ytdShortTermGains={taxMetrics?.ytdStPnl}
                ytdLongTermGains={taxMetrics?.ytdLtPnl}
                isTaxMetricsLoading={isTaxMetricsLoading}
                nextWithdrawal={nextWithdrawal}
                isWithdrawalLoading={isWithdrawalLoading}
                tradingPath={`${AccountPageRoutePath(globalAccountGUID, accountGroupGUID, accountGUID)}/allocations`}
            />
            <TopPositionsCard
                accountGUID={accountGUID}
                positionsPath={`${AccountPageRoutePath(globalAccountGUID, accountGroupGUID, accountGUID)}/positions`}
            />
        </div>
    );
};
